// frontend/src/components/user/Login.js
import React, { useEffect, useState, Fragment } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearAuthError, login } from '../../actions/userActions';
import { toast } from 'react-toastify';
import { Mail, Lock, Eye, EyeOff, LogIn, User } from 'lucide-react';
import MetaData from '../layouts/MetaData';
import Loader from '../layouts/Loader';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { loading, error, isAuthenticated } = useSelector(state => state.authState);
    const redirect = location.search ? '/' + location.search.split('=')[1] : '/';

    useEffect(() => {
        if (isAuthenticated) {
            navigate(redirect);
        }
        if (error) {
            toast.error(error);
            dispatch(clearAuthError());
        }
    }, [error, dispatch, isAuthenticated, navigate, redirect]);

    const submitHandler = (e) => {
        e.preventDefault();
        dispatch(login(email, password));
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <MetaData title="Login" />
            <div 
                className="min-h-screen flex items-center justify-center py-8"
                style={{
                    backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="max-w-md w-full mx-4 bg-white bg-opacity-95 p-6 rounded-lg shadow-sm">
                    <div className="text-center mb-4">
                        <div className="flex items-center justify-center mb-2">
                            <User className="h-8 w-8 text-[#dc2626]" />
                        </div>
                        <h1 className="font-['Crimson_Text'] text-2xl font-bold text-[#dc2626] mb-1">Welcome Back!</h1>
                        <p className="text-sm text-gray-700">Please login to your account</p>
                    </div>

                    <form className="space-y-3" onSubmit={submitHandler}>
                        <div className="space-y-3">
                            {/* Email Field */}
                            <div>
                                <label htmlFor="email_field" className="block text-xs font-medium text-gray-700 mb-1">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-4 w-4 text-[#dc2626]" />
                                    </div>
                                    <input
                                        type="email"
                                        id="email_field"
                                        className="h-10 appearance-none block w-full pl-10 pr-3 py-2 text-center border border-gray-300 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors duration-300 text-sm placeholder:text-center"
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Password Field */}
                            <div>
                                <label htmlFor="password_field" className="block text-xs font-medium text-gray-700 mb-1">
                                    Password
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-4 w-4 text-[#dc2626]" />
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password_field"
                                        className="h-10 appearance-none block w-full pl-10 pr-10 py-2 text-center border border-gray-300 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors duration-300 text-sm placeholder:text-center"
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showPassword ? 
                                            <EyeOff className="h-4 w-4 text-[#dc2626] hover:text-[#dc2626]/80 transition-colors duration-200" /> :
                                            <Eye className="h-4 w-4 text-[#dc2626] hover:text-[#dc2626]/80 transition-colors duration-200" />
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end">
                            <Link
                                to="/password/forgot"
                                className="text-xs font-medium text-[#dc2626] hover:text-[#dc2626]/80 transition-colors duration-200"
                            >
                                Forgot Password?
                            </Link>
                        </div>

                        <div className="mt-4">
                            <button
                                type="submit"
                                className="group relative w-full h-10 flex justify-center items-center px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] transform transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Logging in...
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center">
                                        <LogIn className="h-4 w-4 mr-2" />
                                        Login to Continue
                                    </span>
                                )}
                            </button>
                        </div>

                        <div className="text-center mt-3">
                            <span className="text-gray-600 text-xs">Don't have an account? </span>
                            <Link 
                                to="/register" 
                                className="text-[#dc2626] hover:text-[#dc2626]/80 font-medium transition-colors duration-200 text-xs"
                            >
                                Register here
                            </Link>
                        </div>
                    </form>

                    <div className="mt-3">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500 text-xs">
                                    Protected by MeatBuddy
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default Login;